import React from 'react';
import JamDesktopBar, { JAM_DESKTOP_BAR_H, useIsDesktopJam } from './JamDesktopBar';

const p = (px: number) => `${(px / 402 * 100).toFixed(3)}%`;

interface JamHeaderProps {
  collectionName: string;
  currentSongTitle: string;
  canNavigatePrev: boolean;
  canNavigateNext: boolean;
  onNavigatePrev: () => void;
  onNavigateNext: () => void;
  onOpenChart?: () => void;
  onOpenSetlist?: () => void;
  showEditionModeButton?: boolean;
  onEditionMode?: () => void;
  onSettings?: () => void;
  isHost: boolean;
  sessionCode?: string;       // HOST only — displayed in banner
  musicianCount?: number;     // HOST only — displayed in banner
  hostName?: string;          // CLIENT only — displayed in banner
  isJamMenuOpen?: boolean;    // HOST only — controls arrow rotation
  onToggleJamMenu?: () => void; // HOST only — opens/closes the overlay panel
  jamMenuContent?: React.ReactNode; // HOST only — panel rendered inside wrapper at top:26px
}

const TitlePill: React.FC<{
  currentSongTitle: string;
  canNavigatePrev: boolean;
  canNavigateNext: boolean;
  onNavigatePrev: () => void;
  onNavigateNext: () => void;
  topPx: number;
}> = ({ currentSongTitle, canNavigatePrev, canNavigateNext, onNavigatePrev, onNavigateNext, topPx }) => (
  <div style={{ position: 'absolute', left: p(11), top: `${topPx}px`, width: p(380), height: '34px' }}>
    <div style={{ position: 'absolute', left: 0, top: '2px', width: '100%', height: '24px', backgroundColor: '#D9D9D9', borderRadius: '23px' }} />
    <button onClick={onNavigatePrev} disabled={!canNavigatePrev} title="Previous song"
      style={{ position: 'absolute', left: '8px', top: '2px', height: '24px', background: 'none', border: 'none', padding: '0 4px', cursor: canNavigatePrev ? 'pointer' : 'default', opacity: canNavigatePrev ? 1 : 0.3, zIndex: 1 }}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#271B13" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
    </button>
    <span style={{
      position: 'absolute', left: '50px', top: '2.67px',
      width: 'calc(100% - 100px)', height: '23.33px',
      fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 400, fontSize: '24px',
      fontVariantCaps: 'small-caps', color: '#271B13',
      textAlign: 'center', lineHeight: '23.33px',
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    }}>
      {currentSongTitle || 'NO SONG SELECTED'}
    </span>
    <button onClick={onNavigateNext} disabled={!canNavigateNext} title="Next song"
      style={{ position: 'absolute', right: '8px', top: '2px', height: '24px', background: 'none', border: 'none', padding: '0 4px', cursor: canNavigateNext ? 'pointer' : 'default', opacity: canNavigateNext ? 1 : 0.3, zIndex: 1 }}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#271B13" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
    </button>
  </div>
);

const JamHeader: React.FC<JamHeaderProps> = ({
  collectionName,
  currentSongTitle,
  canNavigatePrev,
  canNavigateNext,
  onNavigatePrev,
  onNavigateNext,
  onOpenChart,
  onOpenSetlist,
  showEditionModeButton = false,
  onEditionMode,
  onSettings,
  isHost,
  sessionCode,
  musicianCount,
  hostName,
  isJamMenuOpen = false,
  onToggleJamMenu,
  jamMenuContent,
}) => {
  const isDesktop = useIsDesktopJam();

  if (isHost) {
    // Mobile banner is 26px, Figma desktop bar is 40px — everything below shifts with it
    const bannerH = isDesktop ? JAM_DESKTOP_BAR_H : 26;

    return (
      <div style={{ position: 'relative', width: '100%', height: `${bannerH + 105}px` }}>

        {isDesktop ? (
          <div style={{ position: 'absolute', left: 0, top: 0, right: 0 }}>
            <JamDesktopBar
              isHost
              sessionCode={sessionCode}
              musicianCount={musicianCount}
              isJamMenuOpen={isJamMenuOpen}
              onToggleJamMenu={onToggleJamMenu}
              jamMenuContent={jamMenuContent}
            />
          </div>
        ) : (
        /* Red banner HOST — group #460:379 — 402×26, layoutMode:none → position:absolute children */
        <div style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '26px', backgroundColor: '#E4393F', overflow: 'hidden' }}>
          {/* JAM group #443:355 — x:20, y:5 */}
          <div style={{ position: 'absolute', left: p(20), top: '5px', width: '48px', height: '17px' }}>
            <img src="/figma-assets/icons/jam-header/jam-icon.svg" width="15" height="17" alt="" style={{ display: 'block' }} />
            <span style={{ position: 'absolute', left: '25px', top: '2px', fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 500, fontSize: '10px', color: '#FFFFFF', whiteSpace: 'nowrap' }}>JAM</span>
          </div>
          {/* HOSTING group #443:354 — x:83, y:7 */}
          <div style={{ position: 'absolute', left: p(83), top: '7px', width: '58px', height: '15px' }}>
            <span style={{ position: 'absolute', left: 0, top: 0, fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 500, fontSize: '10px', color: '#FFFFFF', whiteSpace: 'nowrap' }}>HOSTING</span>
            <span style={{ position: 'absolute', left: '50px', top: '2px', display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#34C759' }} />
          </div>
          {/* CODE #439:210 — x:156, y:7 */}
          <div style={{ position: 'absolute', left: p(156), top: '7px', fontFamily: 'Kumbh Sans, sans-serif', fontSize: '10px', color: '#FFFFFF', whiteSpace: 'nowrap' }}>
            <span style={{ fontWeight: 500 }}>CODE : </span>
            <span style={{ fontWeight: 700, color: '#FFC2C4' }}>{sessionCode || '------'}</span>
          </div>
          {/* MUSICIANS #439:211 — x:259, y:7 */}
          <div style={{ position: 'absolute', left: p(259), top: '7px', fontFamily: 'Kumbh Sans, sans-serif', fontSize: '10px', color: '#FFFFFF', whiteSpace: 'nowrap' }}>
            <span style={{ fontWeight: 500 }}>MUSICIANS : </span>
            <span style={{ fontWeight: 700, color: '#FFE5E6' }}>{musicianCount ?? '—'}</span>
          </div>
          {/* keyboard_arrow_up #439:182 — x:355, y:0, 26×26 */}
          <button onClick={onToggleJamMenu} style={{ position: 'absolute', left: p(355), top: 0, background: 'none', border: 'none', padding: 0, cursor: 'pointer', transform: isJamMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
            <img src="/figma-assets/icons/jam-header/keyboard-arrow-up.svg" width="26" height="26" alt="Menu" style={{ display: 'block' }} />
          </button>
        </div>
        )}

        {/* Header bar — #439:96: y:25, 402×48, #3D3028 (identique GigHeader) */}
        <div style={{ position: 'absolute', left: 0, top: `${bannerH - 1}px`, width: '100%', height: '48px', backgroundColor: '#3D3028' }}>
          {/* Back — #439:120: x:19, y:37(frame)→y:12(bar), 24.64×24.64 */}
          {showEditionModeButton && onEditionMode && (
            <button onClick={onEditionMode} title="Return to Edition Mode"
              style={{ position: 'absolute', left: p(19), top: '12px', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
              <img src="/figma-assets/icons/gig-header/back-header.svg" width="25" height="25" alt="Edition Mode" style={{ display: 'block' }} />
            </button>
          )}
          {/* OPEN composite — #439:123: x:91, y:12 from bar, 76.96×30.69 */}
          {onOpenChart && (
            <button onClick={onOpenChart} title="Open Chart"
              style={{ position: 'absolute', left: p(91), top: '12px', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
              <img src="/figma-assets/icons/gig-header/open-composite.svg" width="77" height="31" alt="Open" style={{ display: 'block' }} />
            </button>
          )}
          {/* SETLISTS composite — #439:132: x:216, y:13.4 from bar, 93.42×24.64 */}
          {onOpenSetlist && (
            <button onClick={onOpenSetlist} title="Open Setlist"
              style={{ position: 'absolute', left: p(216), top: '13px', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
              <img src="/figma-assets/icons/gig-header/setlists-composite.svg" width="94" height="25" alt="Setlists" style={{ display: 'block' }} />
            </button>
          )}
          {/* Hamburger — #439:147: x:357, y:16 from bar, 27×19.2 */}
          {onSettings && (
            <button onClick={onSettings} title="Menu"
              style={{ position: 'absolute', left: p(357), top: '16px', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
              <img src="/figma-assets/icons/gig-header/hamburger-header.svg" width="27" height="20" alt="Menu" style={{ display: 'block' }} />
            </button>
          )}
        </div>

        {/* Setlist name — #439:99: x:57, y:73, 289×29, Kumbh Sans 500 13px SMALL_CAPS 0.17em #D3CCBD */}
        <div style={{
          position: 'absolute', left: p(57), top: `${bannerH + 47}px`, width: p(289), height: '29px',
          fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 500, fontSize: '13px',
          letterSpacing: '0.17em', fontVariantCaps: 'small-caps', color: '#D3CCBD',
          textAlign: 'center', lineHeight: '29px',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {collectionName || 'NO SETLIST'}
        </div>

        {/* Title pill — #439:98: x:11, y:97, 380×34 */}
        <TitlePill
          currentSongTitle={currentSongTitle}
          canNavigatePrev={canNavigatePrev}
          canNavigateNext={canNavigateNext}
          onNavigatePrev={onNavigatePrev}
          onNavigateNext={onNavigateNext}
          topPx={bannerH + 71}
        />

        {/* Panel ancré ici — juste sous la bannière, dans ce même wrapper relative (mobile ; desktop géré par JamDesktopBar) */}
        {!isDesktop && isJamMenuOpen && jamMenuContent && (
          <div style={{ position: 'absolute', top: '26px', left: 0, right: 0, zIndex: 100, maxHeight: 'calc(100vh - 26px)', overflowY: 'auto', animation: 'jam-menu-open 0.2s ease' }}>
            {jamMenuContent}
          </div>
        )}
      </div>
    );
  }

  // ── CLIENT layout ─────────────────────────────────────────────────────────
  // Mobile banner is 34px, Figma desktop bar is 40px
  const bannerH = isDesktop ? JAM_DESKTOP_BAR_H : 34;

  return (
    <div style={{ position: 'relative', width: '100%', height: `${bannerH + 55}px` }}>

      {isDesktop ? (
        <div style={{ position: 'absolute', left: 0, top: 0, right: 0 }}>
          <JamDesktopBar
            isHost={false}
            hostName={hostName}
            isJamMenuOpen={isJamMenuOpen}
            onToggleJamMenu={onToggleJamMenu}
            jamMenuContent={jamMenuContent}
          />
        </div>
      ) : (
      /* Red banner CLIENT — #442:295: 402×34, #E4393F, y:0 */
      <div style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '34px', backgroundColor: '#E4393F' }}>
        {/* Exit button #442:308 — x:18, y:5, 24.64×24.64, fill #DADEE0 — opens 2-choice panel per designer annotation */}
        <button onClick={onToggleJamMenu}
          style={{ position: 'absolute', left: p(18), top: '5px', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
          <img src="/figma-assets/icons/jam-header/back-client.svg" width="25" height="25" alt="Leave" style={{ display: 'block' }} />
        </button>
        {/* JAM icon — #442:336: x:123, y:7, 15×17 */}
        <img src="/figma-assets/icons/jam-header/jam-icon.svg" width="15" height="17" alt=""
          style={{ position: 'absolute', left: p(123), top: '7px', display: 'block' }} />
        {/* "YOU JAM WITH hostname" — #442:340: x:148, y:10 */}
        <span style={{
          position: 'absolute', left: p(148), top: '10px',
          fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 500, fontSize: '10px',
          color: '#FFFFFF', whiteSpace: 'nowrap',
        }}>
          YOU JAM WITH {hostName || '—'}
        </span>
      </div>
      )}

      {/* Setlist name — #442:294: x:57, y:31, 289×29 */}
      <div style={{
        position: 'absolute', left: p(57), top: `${bannerH - 3}px`, width: p(289), height: '29px',
        fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 500, fontSize: '13px',
        letterSpacing: '0.17em', fontVariantCaps: 'small-caps', color: '#D3CCBD',
        textAlign: 'center', lineHeight: '29px',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {collectionName || 'NO SETLIST'}
      </div>

      {/* Title pill — #442:293: x:11, y:55, 380×34 (clients can't navigate) */}
      <TitlePill
        currentSongTitle={currentSongTitle}
        canNavigatePrev={false}
        canNavigateNext={false}
        onNavigatePrev={onNavigatePrev}
        onNavigateNext={onNavigateNext}
        topPx={bannerH + 21}
      />

      {/* CLIENT control panel — sous la bannière rouge (mobile ; desktop géré par JamDesktopBar) */}
      {!isDesktop && isJamMenuOpen && jamMenuContent && (
        <div style={{ position: 'absolute', top: '34px', left: 0, right: 0, zIndex: 100, maxHeight: 'calc(100vh - 34px)', overflowY: 'auto', animation: 'jam-menu-open 0.2s ease' }}>
          {jamMenuContent}
        </div>
      )}
    </div>
  );
};

export default JamHeader;

